import { useEffect, useState } from "react";
import { Star, Quote } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { pickBg } from "@/assets/generated";
import { EditorialHeading } from "./editorial-heading";

const items = [
  { name: "Avv. Laura Bianchi", role: "Partner", firm: "Studio Legale Bianchi & Partners", quote: "LexAIPro ha trasformato il modo in cui gestiamo la ricerca. Quello che prima richiedeva ore ora richiede minuti.", color: "bg-primary" },
  { name: "Dr. Andrea Conti", role: "In-house Counsel", firm: "TechCorp SpA", quote: "Finalmente uno strumento AI che capisce il diritto italiano. Le citazioni sono sempre precise.", color: "bg-accent text-accent-foreground" },
  { name: "Avv. Marco Romano", role: "Associato", firm: "Grande Studio Internazionale", quote: "Il modulo di redazione mi ha fatto risparmiare settimane di lavoro su una serie di contratti complessi.", color: "bg-success text-white" },
  { name: "Avv. Giulia Ferri", role: "Solo practitioner", firm: "Studio Ferri", quote: "Da quando uso LexAIPro ho potuto seguire più clienti senza sacrificare la qualità del lavoro.", color: "bg-destructive text-white" },
  { name: "Dr. Matteo Greco", role: "Head of Legal", firm: "FinHub Italia", quote: "L'analisi documenti è quasi magica. Identifica clausole problematiche che ci sarebbero sfuggite.", color: "bg-primary" },
  { name: "Avv. Sofia De Luca", role: "Associata Senior", firm: "Studio De Luca & Co.", quote: "Lo consiglio ad ogni collega. È come avere uno stagista brillante disponibile 24/7.", color: "bg-warning text-white" },
];

export function TestimonialsSection() {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setIdx((v) => (v + 1) % items.length), 4000);
    return () => clearInterval(i);
  }, []);

  return (
    <section className="relative overflow-hidden bg-background py-16 sm:py-28">
      <img src={pickBg(11)} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-10 dark:opacity-20" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background/85 to-background" />
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="aurora-orb absolute left-1/2 top-0 -translate-x-1/2" style={{ width: 600, height: 400, background: "radial-gradient(ellipse at 50% 0%, hsl(var(--aurora-1) / 0.45), transparent 60%)" }} />
      </div>
      <div className="relative mx-auto max-w-5xl px-5 sm:px-6 lg:px-8">
        <EditorialHeading
          index="No. 07"
          eyebrow={t("testimonials.eyebrow")}
          title={t("testimonials.title")}
        />

        <div className="relative mt-10 min-h-[18rem] overflow-hidden rounded-2xl border border-border bg-card p-5 shadow-lg sm:mt-12 sm:h-60 sm:min-h-0 sm:p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-0 flex flex-col items-center justify-center p-5 text-center sm:p-8"
            >
              <Quote className="h-7 w-7 text-accent opacity-30 sm:h-8 sm:w-8" />
              <p className="mt-3 max-w-2xl font-display text-[17px] italic leading-snug text-foreground sm:mt-4 sm:text-2xl text-balance">
                "{items[idx].quote}"
              </p>
              <div className="mt-6 flex items-center gap-3">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold text-primary-foreground ${items[idx].color}`}>
                  {items[idx].name.split(" ").slice(-2).map((s) => s[0]).join("")}
                </div>
                <div className="text-left">
                  <div className="text-sm font-semibold">{items[idx].name}</div>
                  <div className="text-xs text-muted-foreground">{items[idx].role} · {items[idx].firm}</div>
                </div>
                <div className="ml-3 flex">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-accent text-accent" />
                  ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="mt-6 flex justify-center gap-1.5">
          {items.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`${t("testimonials.aria")} ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-primary" : "w-1.5 bg-border"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
